import React, { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";
import { 
  FaSpinner, 
  FaLocationArrow, 
  FaMapMarkerAlt, 
  FaCompass, 
  FaCalendarAlt, 
  FaSearch, 
  FaCity, 
  FaHistory 
} from "react-icons/fa";

const customStyles = `
  .page-header-gradient {
    background: linear-gradient(135deg, #ffffff 0%, #fff7ed 100%);
    border-radius: 16px;
    padding: 24px;
    border: 1px solid #fed7aa;
    margin-bottom: 24px;
  }
  .stat-card {
    border: none;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.1);
    transition: all 0.3s ease;
    overflow: hidden;
  }
  .stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(249, 115, 22, 0.15);
  }
  .click-badge-orange {
    background: linear-gradient(135deg, #f97316, #ea580c);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.5rem;
    line-height: 1;
  }
  .click-badge-red {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.5rem;
    line-height: 1;
  }
  .click-badge-purple {
    background: linear-gradient(135deg, #8b5cf6, #6d28d9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 800;
    font-size: 2.5rem;
    line-height: 1;
  }
  .table-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.08);
    border: 1px solid #f1f5f9;
    overflow: hidden;
  }
`;

const NearbySearchClicks = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    summary: { total_clicks: 0, near_me_clicks: 0, nearest_city_clicks: 0 },
    by_city: [],
    recent_clicks: []
  });

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedButton, setSelectedButton] = useState("all");

  const axiosAPI = useAxios();

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (selectedCity) params.append("city", selectedCity);

      const res = await axiosAPI.get(`/api/restaurant-clicks/nearby-search-stats?${params.toString()}`);
      if (res?.data?.success) {
        setStats(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching nearby search stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [startDate, endDate, selectedCity]);

  // Filter recent clicks by selected button in table
  const filteredClicks = (stats.recent_clicks || []).filter((c) => {
    if (selectedButton === "all") return true;
    return c.button_type === selectedButton;
  });

  return (
    <div className="container py-4">
      <style>{customStyles}</style>
      <div className="page-inner">

        {/* Page Header */}
        <div className="page-header-gradient d-flex flex-wrap justify-content-between align-items-center shadow-sm gap-3">
          <div>
            <h3 className="fw-bold mb-1" style={{ color: "#1e293b" }}>
              <FaLocationArrow className="text-warning me-2" />
              Location Search Clicks
            </h3>
            <p className="text-muted mb-0">
              Track live clicks on <strong>"Find Restaurants Near Me"</strong> & <strong>"Find Nearest Restaurants"</strong> buttons.
            </p>
          </div>

          {/* Date & Search Filters */}
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="input-group" style={{ maxWidth: "200px" }}>
              <span className="input-group-text bg-white border-end-0">
                <FaSearch className="text-muted small" />
              </span>
              <input
                type="text"
                className="form-control bg-white border-start-0"
                placeholder="Filter by city..."
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                style={{ borderRadius: "0 10px 10px 0" }}
              />
            </div>

            <div className="d-flex align-items-center gap-1 bg-white p-1 rounded-3 border shadow-sm">
              <input
                type="date"
                className="form-control form-control-sm border-0"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                title="Start Date"
              />
              <span className="text-muted small px-1">to</span>
              <input
                type="date"
                className="form-control form-control-sm border-0"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                title="End Date"
              />
            </div>

            {(startDate || endDate || selectedCity) && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-pill"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setSelectedCity("");
                }}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Top 3 KPI Cards */}
        <div className="row g-4 mb-4">
          {/* Card 1: Find Restaurants Near Me (Within 15 km) */}
          <div className="col-lg-4 col-md-6">
            <div className="stat-card p-4 h-100 border-start border-4 border-warning">
              <h6 className="fw-semibold text-secondary mb-1">
                Find Restaurants Near Me (Within 15 km)
              </h6>
              <small className="text-muted d-block mb-3">Triggered on <code>/restaurants</code> hero section</small>
              <div className="d-flex align-items-baseline gap-2">
                <span className="click-badge-orange">{stats.summary?.near_me_clicks || 0}</span>
                <span className="text-muted fw-semibold">Clicks</span>
              </div>
            </div>
          </div>

          {/* Card 2: Find Nearest Restaurants (City Page) */}
          <div className="col-lg-4 col-md-6">
            <div className="stat-card p-4 h-100 border-start border-4 border-danger">
              <h6 className="fw-semibold text-secondary mb-1">
                Find Nearest Restaurants
              </h6>
              <small className="text-muted d-block mb-3">Triggered on <code>/restaurants/:city</code> pages</small>
              <div className="d-flex align-items-baseline gap-2">
                <span className="click-badge-red">{stats.summary?.nearest_city_clicks || 0}</span>
                <span className="text-muted fw-semibold">Clicks</span>
              </div>
            </div>
          </div>

          {/* Card 3: Total Combined Location Searches */}
          <div className="col-lg-4 col-md-12">
            <div className="stat-card p-4 h-100 border-start border-4 border-primary">
              <h6 className="fw-semibold text-secondary mb-1">
                Total Location Search Clicks
              </h6>
              <small className="text-muted d-block mb-3">Combined searches across all restaurant pages</small>
              <div className="d-flex align-items-baseline gap-2">
                <span className="click-badge-purple">{stats.summary?.total_clicks || 0}</span>
                <span className="text-muted fw-semibold">Total Clicks</span>
              </div>
            </div>
          </div>
        </div>

        {/* City Breakdown Pills */}
        {stats.by_city && stats.by_city.length > 0 && (
          <div className="table-card p-3 mb-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <FaCity className="text-primary" />
              <h6 className="fw-bold mb-0 text-dark">Top Cities by "Find Nearest" Clicks:</h6>
            </div>
            <div className="d-flex flex-wrap gap-2 pt-1">
              {stats.by_city.map((c, i) => (
                <span key={i} className="badge bg-light text-dark border px-3 py-2 rounded-pill d-inline-flex align-items-center gap-2">
                  <span className="fw-semibold">{c.city_name}</span>
                  <span className="badge bg-danger rounded-pill">{c.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Click History Table */}
        <div className="table-card p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <div>
              <h5 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                <FaHistory className="text-secondary" />
                Recent Click Logs ({filteredClicks.length})
              </h5>
              <small className="text-muted">Live event stream of user button clicks</small>
            </div>

            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                value={selectedButton}
                onChange={(e) => setSelectedButton(e.target.value)}
                style={{ borderRadius: "8px", minWidth: "220px" }}
              >
                <option value="all">All Buttons</option>
                <option value="near_me_restaurants">Find Restaurants Near Me (Within 15 km)</option>
                <option value="nearest_city_restaurants">Find Nearest Restaurants (City Page)</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <FaSpinner className="spinner fs-2 text-warning mb-2" spin />
              <p className="text-muted">Loading click activity...</p>
            </div>
          ) : filteredClicks.length === 0 ? (
            <div className="text-center py-5">
              <div className="p-3 bg-light rounded-circle d-inline-block mb-3 text-muted">
                <FaLocationArrow className="fs-1 opacity-50" />
              </div>
              <h5 className="text-muted fw-bold">No Click Records Found</h5>
              <p className="text-muted small mb-0">When visitors click the nearby restaurant buttons on the website, clicks will be recorded here.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>Button</th>
                    <th>City / Location</th>
                    <th>GPS Coordinates</th>
                    <th>Page URL</th>
                    <th>Click Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClicks.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="text-muted">{index + 1}</td>
                      <td>
                        <span
                          className="badge rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                          style={
                            item.button_type === "near_me_restaurants"
                              ? {
                                  backgroundColor: "#f59e0b",
                                  color: "#1f2937",
                                  border: "1px solid #d97706",
                                  fontSize: "0.82rem"
                                }
                              : {
                                  backgroundColor: "#ef4444",
                                  color: "#ffffff",
                                  border: "1px solid #dc2626",
                                  fontSize: "0.82rem"
                                }
                          }
                        >
                          {item.button_type === "near_me_restaurants" ? (
                            <FaLocationArrow className="me-1" />
                          ) : (
                            <FaMapMarkerAlt className="me-1 text-white" />
                          )}
                          {item.button_label && item.button_label.trim()
                            ? item.button_label
                            : item.button_type === "nearest_city_restaurants"
                            ? "Find Nearest Restaurants"
                            : "Find Restaurants Near Me (Within 15 km)"}
                        </span>
                      </td>
                      <td className="fw-bold text-dark">
                        {item.city_name ? (
                          <span className="badge bg-light text-dark border px-2.5 py-1.5 rounded-pill" title={item.city_name}>
                            📍 {(() => {
                              if (!item.city_name.includes(",")) return item.city_name;
                              const parts = item.city_name.split(",").map(p => p.trim());
                              return parts.length >= 3 ? parts[parts.length - 3] : parts[0];
                            })()}
                          </span>
                        ) : (
                          <span className="text-muted small">All Cities (General)</span>
                        )}
                      </td>
                      <td>
                        {item.latitude && item.longitude ? (
                          <span
                            className="badge bg-light text-dark border px-2.5 py-1.5 rounded-pill font-monospace"
                            title={`Lat: ${item.latitude}, Lng: ${item.longitude}`}
                            style={{ fontSize: "0.78rem" }}
                          >
                            🌐 {Number(item.latitude).toFixed(4)}, {Number(item.longitude).toFixed(4)}
                          </span>
                        ) : (
                          <span className="text-muted small">Not shared</span>
                        )}
                      </td>
                      <td>
                        <code className="text-muted small">{item.page_url || "/restaurants"}</code>
                      </td>
                      <td className="text-muted small">
                        <FaCalendarAlt className="me-1 opacity-50" />
                        {item.click_date ? (() => {
                          const str = String(item.click_date).trim();
                          const utcIso = str.includes("T")
                            ? (str.endsWith("Z") ? str : str + "Z")
                            : str.replace(" ", "T") + "Z";
                          const dt = new Date(utcIso);
                          return !isNaN(dt.getTime())
                            ? dt.toLocaleString("en-IN", {
                                timeZone: "Asia/Kolkata",
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true
                              })
                            : str;
                        })() : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default NearbySearchClicks;
